import React from 'react';
import PropTypes from 'prop-types';
import { VictoryChart, VictoryLine, VictoryStack, VictoryLegend, VictoryAxis, VictoryContainer } from "victory";
import { colors, monthYearStringToDate, abbreviateNumber } from '../ReportsHelper';
import NoData from '../NoData';
import ReportChart from './ReportChart';
import { ReportChartConsts } from '../ReportTooltips';

export default function BoundPremiumByLobChart({ data }) {
    const labelColor = document.documentElement.getAttribute('data-gdstheme') === 'dark' ? "#ffffff" : "#000000";
    const noData = !data?.some(group => group.GroupedData?.length || group.Data?.length ||
        Object.keys(group).map(key => Object.entries(group[key])).every(([key, value]) => value !== 0));

    if (noData) {
        return (
            <ReportChart {...ReportChartConsts('nbwpByMonth')}>
                <NoData />
            </ReportChart>
        );
    }

    data.forEach(lobData => {
        lobData.GroupedData?.forEach(monthData => {
            monthData.GroupingKey = monthYearStringToDate(monthData.GroupingKey);
        });
    });

    const completeMonthRange = (() => {
        const months = [];
        const allDates = data.flatMap(g => g.GroupedData.map(item => item.GroupingKey));
        const current = new Date(Math.min(...allDates));
        const maxDate = new Date(Math.max(...allDates));
        while (current <= maxDate) {
            months.push(new Date(current));
            current.setMonth(current.getMonth() + 1);
        }
        const step = Math.ceil(months.length / 8);
        return months.filter((_, index) => index % step === 0);
    })();

    return (
        <ReportChart {...ReportChartConsts('nbwpByMonth')}>
            {noData ? <NoData /> :
                <VictoryChart
                    containerComponent={
                        <VictoryContainer
                            responsive={true}
                            preserveAspectRatio={"none"}
                        />
                    }
                >
                    <VictoryLegend
                        y={10}
                        x={20}
                        orientation="horizontal"
                        gutter={20}
                        style={{ labels: { fontSize: 12, fill: labelColor } }}
                        data={data.map((group, index) => ({ name: group.GroupingKey, symbol: { fill: colors[index] } }))}
                    />
                    <VictoryAxis crossAxis
                        style={{
                            tickLabels: { fontSize: 10, fill: labelColor },
                            axis: { stroke: labelColor },

                        }}
                        tickValues={completeMonthRange}
                        tickFormat={(x) => new Date(x).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '\n')}
                    />
                    <VictoryAxis dependentAxis
                        domain={{ y: [0, Math.max(...data.map(d => Math.max(...d.GroupedData.map(item => item.Data.BoundPremium)))) * 1.5] }}
                        style={{
                            axis: { stroke: labelColor },
                            tickLabels: {
                                fontSize: 10,
                                fill: labelColor,
                            },
                            grid: {
                                stroke: "#d9d9d9",
                                size: 5,
                            }
                        }}
                        tickFormat={(x) => `$${abbreviateNumber(x)}`}
                    />
                    <VictoryStack colorScale={colors}>
                        {
                            data.map((d, index) => (
                                <VictoryLine
                                    key={d.GroupingKey}
                                    interpolation="cardinal"
                                    data={d.GroupedData.map((item) => ({
                                        x: item.GroupingKey,
                                        y: item.Data.BoundPremium,
                                    }))}
                                    style={{
                                        data: {
                                            stroke: [colors[index]],
                                        },
                                    }}
                                />
                            ))
                        }
                    </VictoryStack>
                </VictoryChart>
            }
        </ReportChart>
    );
}

BoundPremiumByLobChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        GroupingKey: PropTypes.string,
        GroupedData: PropTypes.arrayOf(PropTypes.shape({
            GroupingKey: PropTypes.string,
            Data: PropTypes.shape({
                BoundPremium: PropTypes.number
            })
        }))
    }))
};
